/**
 * Month Modal Component
 * Shows full month calendar view with meal assignments
 */

import React, { useState, useMemo } from 'react'
import { MealAssignment, RecipeRecord, formatDateKey } from '@talktoast/shared'
import { getMealTypeConfig } from './calendarConstants'

interface MonthModalProps {
  isOpen: boolean
  onClose: () => void
  assignments: Map<string, MealAssignment>
  weekOffset: number
  onNavigateToWeek: (weekOffset: number, dateStr: string) => void
  onRecipeClick: (assignment: MealAssignment) => void
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const MonthModal: React.FC<MonthModalProps> = ({
  isOpen,
  onClose,
  assignments,
  weekOffset,
  onNavigateToWeek,
  onRecipeClick
}) => {
  const [selectedDatePreview, setSelectedDatePreview] = useState<Date | null>(null)
  const [monthOffset, setMonthOffset] = useState(0)

  // Get month calendar data
  const monthData = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Start from first day of current month + monthOffset months
    const currentMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)

    // Get first day of the month and find the Sunday before it
    const firstDayOfMonth = currentMonth.getDay()
    const startDate = new Date(currentMonth)
    startDate.setDate(startDate.getDate() - firstDayOfMonth)

    // Generate 42 days (6 weeks) to ensure full month coverage
    const monthDays = Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + index)

      const isCurrentMonth = date.getMonth() === currentMonth.getMonth()

      return {
        date: date.getDate(),
        fullDate: date,
        isToday: date.toDateString() === new Date().toDateString(),
        isCurrentMonth
      }
    })

    return {
      month: currentMonth.getMonth(),
      year: currentMonth.getFullYear(),
      days: monthDays
    }
  }, [monthOffset])

  // Helper to get assigned meals for a specific date
  const getMealsForDate = (date: Date) => {
    const dateStr = formatDateKey(date)
    const meals: Array<MealAssignment & { emoji: string }> = []

    assignments.forEach((assignment, key) => {
      // Key format: YYYY-MM-DD_mealType
      const [assignedDateStr] = key.split('_')

      if (assignedDateStr === dateStr) {
        const mealTypeInfo = getMealTypeConfig(assignment.mealType)
        meals.push({
          ...assignment,
          emoji: mealTypeInfo?.emoji || '🍽️'
        })
      }
    })

    return meals
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-24">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => {
          onClose()
          setSelectedDatePreview(null)
        }}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[calc(100vh-8rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => {
                onClose()
                setSelectedDatePreview(null)
                setMonthOffset(0)
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
            <h2 className="text-lg font-bold">
              {new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
                new Date(monthData.year, monthData.month, 1)
              )}
            </h2>
            <div className="w-16"></div> {/* Spacer for centering */}
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setMonthOffset(prev => prev - 1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              ←
            </button>
            <span className="text-sm text-gray-600 min-w-[100px] text-center">
              {monthOffset === 0 ? 'This Month' : monthOffset > 0 ? `${monthOffset} month${monthOffset > 1 ? 's' : ''} ahead` : `${Math.abs(monthOffset)} month${Math.abs(monthOffset) > 1 ? 's' : ''} ago`}
            </span>
            <button
              onClick={() => setMonthOffset(prev => prev + 1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              →
            </button>
          </div>
        </div>

        {/* Month Calendar */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 border-b">
              {DAYS_OF_WEEK.map((day, i) => (
                <div key={i} className="p-2 text-center font-medium text-xs text-gray-700 bg-gray-50">
                  {day}
                </div>
              ))}
            </div>

            {/* Month Grid */}
            <div className="grid grid-cols-7">
              {monthData.days.map((day, index) => {
                const meals = getMealsForDate(day.fullDate)
                const hasAnyMeals = meals.length > 0

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDatePreview(day.fullDate)}
                    className={`min-h-[70px] p-2 border-r border-b transition-colors ${
                      day.isCurrentMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                    } ${
                      day.isToday ? 'bg-blue-50' : ''
                    } ${
                      selectedDatePreview?.toDateString() === day.fullDate.toDateString()
                        ? 'ring-2 ring-blue-500'
                        : ''
                    }`}
                  >
                    {/* Day Number */}
                    <div className={`text-sm font-medium mb-1 ${
                      day.isToday
                        ? 'text-blue-600 font-bold'
                        : day.isCurrentMonth
                        ? 'text-gray-900'
                        : 'text-gray-400'
                    }`}>
                      {day.date}
                    </div>

                    {/* Meal Icons */}
                    {hasAnyMeals && (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {meals.slice(0, 5).map((meal, mealIndex) => (
                          <div
                            key={mealIndex}
                            className="text-base leading-none"
                          >
                            {meal.emoji}
                          </div>
                        ))}
                        {meals.length > 5 && (
                          <div className="text-xs text-gray-500">
                            +{meals.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Day Preview (Bottom Sheet within Modal) */}
        {selectedDatePreview && (
          <div className="border-t bg-gray-50 p-4 animate-slide-up">
            <h3 className="font-bold text-gray-900 mb-3">
              {new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(selectedDatePreview)}
            </h3>

            {(() => {
              const meals = getMealsForDate(selectedDatePreview)
              if (meals.length === 0) {
                return <p className="text-gray-500 text-sm mb-3">No meals planned for this day</p>
              }

              return (
                <div className="space-y-2 mb-3">
                  {meals.map((meal, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        onRecipeClick(meal)
                        onClose()
                        setSelectedDatePreview(null)
                      }}
                      className="flex items-center gap-2 text-sm w-full hover:bg-gray-100 active:bg-gray-200 p-2 rounded transition-colors text-left"
                    >
                      <span className="text-lg">{meal.emoji}</span>
                      <span className="font-medium capitalize">{meal.mealType}:</span>
                      <span className="text-gray-700 underline">{meal.recipeName}</span>
                    </button>
                  ))}
                </div>
              )
            })()}

            <button
              onClick={() => {
                // Calculate week offset based on Sunday-aligned week boundaries,
                // so Saturday→Sunday transitions always land on the correct week.
                const today = new Date()
                today.setHours(0, 0, 0, 0)

                const currentWeekSunday = new Date(today)
                currentWeekSunday.setDate(today.getDate() - today.getDay())
                currentWeekSunday.setHours(0, 0, 0, 0)

                const targetWeekSunday = new Date(selectedDatePreview)
                targetWeekSunday.setDate(selectedDatePreview.getDate() - selectedDatePreview.getDay())
                targetWeekSunday.setHours(0, 0, 0, 0)

                const newWeekOffset = Math.round(
                  (targetWeekSunday.getTime() - currentWeekSunday.getTime()) / (7 * 24 * 60 * 60 * 1000)
                )
                const dateStr = formatDateKey(selectedDatePreview)

                onNavigateToWeek(newWeekOffset, dateStr)
                onClose()
                setSelectedDatePreview(null)
              }}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              Go to This Week
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
